import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, Section } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, UploadSimple, DownloadSimple } from "@phosphor-icons/react";

const emptyItem = () => ({ _rowKey: crypto.randomUUID(), name: "", type: "earning", calculation_type: "Fixed", amount: 0, percent_value: 0, taxable: true, recurring: true });

export default function SalaryStructures() {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [structures, setStructures] = useState([]);
  const [components, setComponents] = useState([]);
  const [form, setForm] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  // Validation errors keyed by field path. Shape:
  //   { effective_from?: string, basic_salary?: string,
  //     earnings: { [idx]: { name?: string, amount?: string, percent_value?: string } },
  //     deductions: { ... } }
  const [errors, setErrors] = useState({ earnings: {}, deductions: {} });

  useEffect(() => {
    Promise.all([api.get("/users/employees"), api.get("/salary-components")])
      .then(([u, c]) => { setUsers(u.data); setComponents(c.data); });
  }, []);

  const downloadTemplate = async () => {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/salary-revisions/template`, {
      credentials: "include",
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = "salary_revision_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true); setImportResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/salary-revisions/import`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Upload failed");
      setImportResult(data);
      toast.success(`${data.created} revision(s) created · ${data.errors.length} error(s)`);
      if (selected) loadStructure(selected);
    } catch (err) { toast.error(err.message); }
    e.target.value = ""; setImporting(false);
  };

  const loadStructure = async (uid) => {
    setSelected(uid);
    const r = await api.get(`/salary-structure/${uid}`);
    setStructures(r.data);
  };

  const openNew = () => {
    const latest = structures[0];
    // Older DB rows (seed data + pre-fix saves) may lack `type` or
    // `component_id` on items. Normalise so the Pydantic validator doesn't
    // reject them when we re-submit.
    const norm = (list, kind) => (list || []).map((it) => {
      const type = it.type || (kind === "earnings" ? "earning" : "deduction");
      const matched = components.find((c) => c.name === it.name && c.type === type);
      return {
        _rowKey: it._rowKey || crypto.randomUUID(),
        name: it.name || "",
        type,
        calculation_type: it.calculation_type || "Fixed",
        amount: it.amount || 0,
        percent_value: it.percent_value ?? 0,
        taxable: it.taxable ?? true,
        recurring: it.recurring ?? true,
        component_id: it.component_id || matched?.id || null,
      };
    });
    setErrors({ earnings: {}, deductions: {} });
    setForm({
      user_id: selected,
      effective_from: new Date().toISOString().slice(0, 10),
      basic_salary: latest?.basic_salary || 30000,
      currency: "INR",
      earnings: latest?.earnings
        ? norm(latest.earnings, "earnings")
        : [{ ...emptyItem(), name: "HRA", calculation_type: "Percent of Basic", percent_value: 40 }],
      deductions: latest?.deductions
        ? norm(latest.deductions, "deductions")
        : [{ ...emptyItem(), type: "deduction", name: "Provident Fund", calculation_type: "Percent of Basic", percent_value: 12 }],
      notes: "",
    });
  };

  const updateItem = (section, idx, patch) => {
    const next = { ...form, [section]: form[section].map((it, i) => i === idx ? { ...it, ...patch } : it) };
    setForm(next);
    // Clear any error on the fields we just touched.
    setErrors((prev) => {
      const row = { ...(prev[section][idx] || {}) };
      for (const k of Object.keys(patch)) delete row[k];
      return { ...prev, [section]: { ...prev[section], [idx]: row } };
    });
  };
  const addItem = (section) => setForm({ ...form, [section]: [...form[section], { ...emptyItem(), type: section === "earnings" ? "earning" : "deduction" }] });
  const removeItem = (section, idx) => {
    setForm({ ...form, [section]: form[section].filter((_, i) => i !== idx) });
    setErrors((prev) => {
      // Reindex errors so row N+1's errors shift to row N after a removal.
      const nextRows = {};
      Object.entries(prev[section]).forEach(([k, v]) => {
        const i = Number(k);
        if (i < idx) nextRows[i] = v;
        else if (i > idx) nextRows[i - 1] = v;
      });
      return { ...prev, [section]: nextRows };
    });
  };

  // Returns { ok: bool, errors: {...}, summary: string[] } for the current form.
  const validate = () => {
    const err = { earnings: {}, deductions: {} };
    const summary = [];
    if (!form.user_id) summary.push("Please select an employee before saving.");
    if (!form.effective_from) {
      err.effective_from = "Effective date is required";
      summary.push("Effective date is required.");
    }
    if (!form.basic_salary || form.basic_salary <= 0) {
      err.basic_salary = "Basic salary must be greater than zero";
      summary.push("Basic salary must be greater than zero.");
    }
    const validateRows = (section, label) => {
      form[section].forEach((it, i) => {
        const rowErr = {};
        if (!it.name || !it.name.trim()) {
          rowErr.name = `Select a ${label} component`;
          summary.push(`${label} row ${i + 1}: component is required.`);
        }
        if (it.calculation_type && it.calculation_type.startsWith("Percent")) {
          if (it.percent_value === "" || it.percent_value == null || Number.isNaN(Number(it.percent_value)) || Number(it.percent_value) <= 0) {
            rowErr.percent_value = "Enter a % greater than 0";
            summary.push(`${label} row ${i + 1} (${it.name || "unnamed"}): percentage must be > 0.`);
          }
        } else if (["Fixed", "Per Day", "Per Hour"].includes(it.calculation_type)) {
          if (it.amount === "" || it.amount == null || Number.isNaN(Number(it.amount)) || Number(it.amount) < 0) {
            rowErr.amount = "Enter a valid amount";
            summary.push(`${label} row ${i + 1} (${it.name || "unnamed"}): amount is required.`);
          }
        }
        if (Object.keys(rowErr).length) err[section][i] = rowErr;
      });
    };
    validateRows("earnings", "Earning");
    validateRows("deductions", "Deduction");
    const hasErr = summary.length > 0;
    return { ok: !hasErr, errors: err, summary };
  };

  const save = async () => {
    if (saving) return; // guard against double-click
    const { ok, errors: fieldErrors, summary } = validate();
    setErrors(fieldErrors);
    if (!ok) {
      // Show up to 3 specific messages so the toast is readable.
      const first = summary.slice(0, 3).join(" ");
      const more = summary.length > 3 ? ` (+${summary.length - 3} more)` : "";
      toast.error(first + more);
      return;
    }
    setSaving(true);
    try {
      // Strip the client-only `_rowKey` before posting so the backend doesn't
      // echo it back in subsequent reads (Pydantic drops unknowns anyway, but
      // this keeps the audit payload clean).
      const strip = (list) => list.map(({ _rowKey, ...rest }) => rest);
      const payload = { ...form, earnings: strip(form.earnings), deductions: strip(form.deductions) };
      await api.post("/salary-structure", payload);
      toast.success("Salary structure saved successfully");
      setForm(null);
      setErrors({ earnings: {}, deductions: {} });
      loadStructure(selected);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const compute = (section, items, basic) => items.map((it) => {
    if (it.calculation_type.startsWith("Percent") && it.percent_value) {
      return { ...it, amount: Math.round(basic * it.percent_value / 100 * 100) / 100 };
    }
    return it;
  });

  return (
    <div data-testid="admin-salary-structures">
      <PageHeader overline="Compensation" title="Salary Structures" subtitle="Create or revise per-employee salary structures."
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={downloadTemplate} className="rounded-none h-9 text-xs" data-testid="dl-template">
              <DownloadSimple size={14} className="mr-1" /> Template
            </Button>
            <label className="inline-flex items-center h-9 px-3 border border-[#1F4E8C] text-[#1F4E8C] cursor-pointer hover:bg-[#1F4E8C] hover:text-white transition-colors text-xs font-semibold" data-testid="import-revisions">
              <UploadSimple size={14} className="mr-1" /> {importing ? "Importing…" : "Bulk Import"}
              <input type="file" accept=".xlsx" onChange={uploadFile} className="hidden" />
            </label>
          </div>
        } />

      {importResult && (
        <div className="mb-4 p-4 border border-[#1F4E8C] bg-[#F7F7F5]" data-testid="import-result">
          <div className="flex items-center justify-between">
            <div>
              <div className="eyebrow">Import summary</div>
              <div className="font-head text-xl font-semibold">
                {importResult.created} revision{importResult.created === 1 ? "" : "s"} created
                {importResult.errors?.length > 0 && <span className="text-red-600"> · {importResult.errors.length} error(s)</span>}
              </div>
            </div>
            <button onClick={() => setImportResult(null)} className="text-sm text-neutral-500 hover:text-[#111]">×</button>
          </div>
          {importResult.errors?.length > 0 && (
            <div className="mt-3 text-xs space-y-1 max-h-40 overflow-y-auto">
              {importResult.errors.map((e, i) => <div key={i} className="text-red-600">Row {e.row}: {e.error}</div>)}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section title={`Employees (${users.length})`}>
          <div className="divide-y divide-[#F0F0EE] max-h-[600px] overflow-y-auto">
            {users.map((u) => (
              <button key={u.id} onClick={() => loadStructure(u.id)}
                data-testid={`emp-row-${u.id}`}
                className={`w-full text-left px-3 py-3 hover:bg-[#F7F7F5] ${selected === u.id ? "bg-[#F7F7F5] border-l-2 border-[#FF5500]" : ""}`}>
                <div className="font-medium text-sm">{u.name}</div>
                <div className="text-xs text-neutral-500">{u.department} · {u.designation}</div>
              </button>
            ))}
          </div>
        </Section>

        <div className="lg:col-span-2 space-y-4">
          {!selected ? (
            <div className="bg-white border border-[#E5E5E5] p-10 text-center text-neutral-500">Select an employee to view their salary history.</div>
          ) : (
            <>
              <Section title="Structure History"
                right={<Button onClick={openNew} className="rounded-none bg-[#FF5500] hover:bg-[#E64D00] text-white h-9" data-testid="new-structure"><Plus size={14} className="mr-1" />New Revision</Button>}>
                {structures.length === 0 ? (
                  <div className="text-neutral-500 text-sm">No salary structure yet. Create one.</div>
                ) : (
                  <div className="space-y-3">
                    {structures.map((s) => (
                      <div key={s.id} className="border border-[#E5E5E5] p-4" data-testid={`structure-${s.id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="eyebrow">Effective from {s.effective_from}</div>
                            <div className="font-head text-2xl font-semibold mt-1">₹{s.basic_salary.toLocaleString()} <span className="text-sm text-neutral-500 font-body font-normal">basic</span></div>
                          </div>
                          <div className="text-right">
                            <div className="eyebrow">Gross</div>
                            <div className="font-head font-semibold">
                              ₹{(s.basic_salary + s.earnings.reduce((a, b) => a + (b.amount || 0), 0)).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-6 mt-4 pt-3 border-t border-[#F0F0EE] text-xs">
                          <div>
                            <div className="eyebrow mb-2">Earnings</div>
                            {s.earnings.map((e, i) => <div key={i} className="flex justify-between py-1"><span>{e.name}</span><span className="font-semibold">₹{e.amount?.toLocaleString()}</span></div>)}
                          </div>
                          <div>
                            <div className="eyebrow mb-2">Deductions</div>
                            {s.deductions.map((e, i) => <div key={i} className="flex justify-between py-1"><span>{e.name}</span><span className="font-semibold">₹{e.amount?.toLocaleString()}</span></div>)}
                          </div>
                        </div>
                        {s.notes && <div className="text-xs text-neutral-500 mt-3 italic">{s.notes}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </>
          )}
        </div>
      </div>

      {form && (
        <Dialog open onOpenChange={(v) => !v && setForm(null)}>
          <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-head">New Salary Structure / Revision</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="eyebrow mb-1">Effective from</div>
                  <Input
                    type="date"
                    value={form.effective_from}
                    onChange={(e) => { setForm({ ...form, effective_from: e.target.value }); setErrors((p) => ({ ...p, effective_from: undefined })); }}
                    className={`rounded-none ${errors.effective_from ? "border-red-500" : ""}`}
                    data-testid="sf-eff"
                  />
                  {errors.effective_from && <div className="text-xs text-red-600 mt-1" data-testid="err-eff">{errors.effective_from}</div>}
                </div>
                <div>
                  <div className="eyebrow mb-1">Basic Salary (₹)</div>
                  <Input
                    type="number"
                    value={form.basic_salary}
                    onChange={(e) => { setForm({ ...form, basic_salary: parseFloat(e.target.value) || 0 }); setErrors((p) => ({ ...p, basic_salary: undefined })); }}
                    className={`rounded-none ${errors.basic_salary ? "border-red-500" : ""}`}
                    data-testid="sf-basic"
                  />
                  {errors.basic_salary && <div className="text-xs text-red-600 mt-1" data-testid="err-basic">{errors.basic_salary}</div>}
                </div>
              </div>

              {["earnings", "deductions"].map((section) => (
                <div key={section}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="eyebrow">{section}</div>
                    <Button size="sm" variant="outline" onClick={() => addItem(section)} className="rounded-none h-8 text-xs" data-testid={`add-${section}`}><Plus size={12} className="mr-1" />Add</Button>
                  </div>
                  <div className="space-y-2">
                    {form[section].map((it, i) => {
                      const rowErr = errors[section][i] || {};
                      return (
                      <div key={it._rowKey} className={`grid grid-cols-2 sm:grid-cols-12 gap-2 items-start border p-2 ${Object.keys(rowErr).length ? "border-red-500 bg-red-50/40" : "border-[#E5E5E5]"}`} data-testid={`${section}-row-${i}`}>
                        <div className="col-span-2 sm:col-span-4">
                          <select value={it.name} onChange={(e) => {
                            const name = e.target.value;
                            const type = section === "earnings" ? "earning" : "deduction";
                            const match = components.find(c => c.name === name && c.type === type);
                            updateItem(section, i, { name, component_id: match?.id || null });
                          }}
                            className={`w-full h-9 border px-2 bg-white text-sm ${rowErr.name ? "border-red-500" : "border-[#E5E5E5]"}`}
                            data-testid={`${section}-name-${i}`}>
                            <option value="">Select component…</option>
                            {components.filter(c => c.type === (section === "earnings" ? "earning" : "deduction")).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                          </select>
                          {rowErr.name && <div className="text-xs text-red-600 mt-1">{rowErr.name}</div>}
                        </div>
                        <select value={it.calculation_type} onChange={(e) => updateItem(section, i, { calculation_type: e.target.value })}
                          className="col-span-2 sm:col-span-3 h-9 border border-[#E5E5E5] px-2 bg-white text-sm">
                          {["Fixed", "Percent of Basic", "Manual", "Per Day", "Per Hour"].map((t) => <option key={t}>{t}</option>)}
                        </select>
                        <div className="col-span-1 sm:col-span-2">
                          {it.calculation_type.startsWith("Percent") ? (
                            <Input type="number" placeholder="%" value={it.percent_value} onChange={(e) => updateItem(section, i, { percent_value: parseFloat(e.target.value) || 0 })} className={`rounded-none h-9 ${rowErr.percent_value ? "border-red-500" : ""}`} data-testid={`${section}-pct-${i}`} />
                          ) : (
                            <Input type="number" placeholder="₹" value={it.amount} onChange={(e) => updateItem(section, i, { amount: parseFloat(e.target.value) || 0 })} className={`rounded-none h-9 ${rowErr.amount ? "border-red-500" : ""}`} data-testid={`${section}-amt-${i}`} />
                          )}
                          {(rowErr.percent_value || rowErr.amount) && <div className="text-xs text-red-600 mt-1">{rowErr.percent_value || rowErr.amount}</div>}
                        </div>
                        <label className="col-span-1 sm:col-span-2 text-xs flex items-center gap-1 whitespace-nowrap h-9">
                          <input type="checkbox" checked={it.taxable} onChange={(e) => updateItem(section, i, { taxable: e.target.checked })} /> Taxable
                        </label>
                        <button onClick={() => removeItem(section, i)} className="col-span-2 sm:col-span-1 text-neutral-500 hover:text-red-600 justify-self-end h-9" data-testid={`${section}-del-${i}`}><Trash size={14} /></button>
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="pt-3 border-t border-[#E5E5E5]">
                <div className="flex justify-between font-semibold text-sm">
                  <span>Gross:</span>
                  <span>₹{(form.basic_salary + compute("earnings", form.earnings, form.basic_salary).reduce((a, b) => a + (b.amount || 0), 0)).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm text-neutral-500">
                  <span>Total Deductions:</span>
                  <span>₹{compute("deductions", form.deductions, form.basic_salary).reduce((a, b) => a + (b.amount || 0), 0).toLocaleString()}</span>
                </div>
              </div>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes / reason for revision" className="rounded-none" />
              <Button onClick={save} disabled={saving} className="w-full rounded-none bg-[#FF5500] hover:bg-[#E64D00] disabled:opacity-60" data-testid="save-structure">{saving ? "Saving…" : "Save Structure"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
